fx.service( class LayoutsGenerator extends fx.EventDispatcher {

    constructor(model)
    {
        super();

        this.model = model.creationModel;



        this._layouts = new Map();
        this._maxCached = 40;
    }

    // read live : the service outlives a format change, and it used to keep the size it was built with
    get _pageWidth()
    {
        return this.model.creation.pageWidth;
    }

    get _pageHeight()
    {
        return this.model.creation.pageHeight;
    }

    getLayouts(photos, fold)
    {
        let stored = this._getStoredLayouts(photos, fold);

        if (stored)
            return stored;

        // the builders read page.margin : the service is a singleton, a field would outlive the call
        let dense = this._isDense(photos.length);
        let gutter = this._foldMargin(fold, dense);
        let page = {x : (fold == "left") ? gutter : 0, y : 0,
                    width : this._pageWidth - gutter, height : this._pageHeight,
                    margin : dense ? this.DENSE_MARGIN : this.PICTURE_MARGIN,
                    outer  : dense ? this.DENSE_PAGE_MARGIN : this.PAGE_MARGIN};

        let combinations = this._combinationsFor(photos);
        let layouts = [];

        layouts = layouts.concat( this._layoutsFor(photos, combinations, page, this.ROWS) );
        layouts = layouts.concat( this._layoutsFor(photos, combinations, page, this.COLUMNS) );

        layouts = this._filterLayouts(layouts, photos);

        // the variants are worth making only for the layouts that survived : building them for the
        // whole pool meant eight thousand band scans to keep thirty layouts, a quarter of the time
        if (this._isDense(photos.length))
            layouts = this._filterLayouts( layouts.concat( this._gutterVariants(layouts, page) ), photos);

        if (this._isDense(photos.length))
            this._reduceMargins(layouts, page);

        layouts = this._improveLayouts(layouts);

        this._storeLayouts(photos, layouts, fold);

        return layouts;
    }

    get _legacyUpTo()
    {
        return fx.get("settings").layoutsLegacyUpTo;
    }

    /** Past the frozen range : a contact sheet, where the newer row counts, ranking and gutters apply. */
    _isDense(count)
    {
        return count > this._legacyUpTo;
    }

    /** The memo key : the photo sizes and their order, the page, and which edge held the fold. */
    _cacheKey(photos, fold)
    {
        return this.getCode(photos) + "@" + this._pageWidth + "x" + this._pageHeight + (fold ? ":" + fold : "");
    }

    _storeLayouts(photos, layouts, fold)
    {
        let key = this._cacheKey(photos, fold);

        this._layouts.delete(key);
        this._layouts.set(key, layouts);

        // one entry of fifty photos holds thirty layouts of fifty components : the oldest go
        while (this._layouts.size > this._maxCached)
            this._layouts.delete(this._layouts.keys().next().value);
    }

    _getStoredLayouts(photos, fold)
    {
        let key = this._cacheKey(photos, fold);
        let layouts = this._layouts.get(key);

        if (layouts)
        {
            this._layouts.delete(key);       // keep it at the fresh end
            this._layouts.set(key, layouts);
        }

        return layouts;
    }

    getCode(elements)
    {
        let code = "";
        for (let i = 0; i < elements.length; i++)
            code += elements[i].width + "x" + elements[i].height + ( (i+1 < elements.length) ? "|" : "");
        return code;
    }


    /** One grid per split, photos in order. _createLayout works it in place, so each axis needs its own. */
    _gridsFor(photos, combinations)
    {
        let grids = [];

        for (let i = 0; i < combinations.length; i++)
        {
            let combination = combinations[i];
            let grid = [];
            let index = 0;

            for (let k = 0; k < combination.length; k++)
            {
                grid[k] = [];

                for (let j = 0; j < combination[k] && index < photos.length; j++)
                {
                    let photo = photos.getItemAt(index++);
                    grid[k].push({ photo : photo, width : photo.width, height : photo.height });
                }
            }

            grids.push(grid);
        }

        return grids;
    }

    /**
     * One layout from one grid of bands, ROWS or COLUMNS : the two builds differ only in which axis
     * is which. Cells keep their photo's ratio throughout, so nothing is ever cropped.
     */
    _createLayout(grid, page, on)
    {
        // first pass : bands justified flush at their natural sizes, then fitted to the page
        let longest = this._evenBandThickness(grid, on);
        this._stretchBands(grid, on, longest);
        this._placeCells(grid, on, 0);
        this._scaleGrid(grid, page);

        // second pass : the same bands re-justified with the gutters counted in, then centered
        longest = this._spaceOutBands(grid, on, page.margin);
        this._stretchBands(grid, on, longest);
        this._placeCells(grid, on, page.margin);

        return this._scaleGrid(grid, page, true).flat();
    }

    /*
     * The four passes below work the grid in place and hand two values to each other on the band
     * arrays : _evenBandThickness and _spaceOutBands write `span`, _stretchBands reads it and
     * writes `thickness`, _placeCells reads that. The order in _createLayout is therefore fixed,
     * and `span` means two different things -- see each one. Both are scratch, dropped at .flat().
     */

    /** Each band down to its thinnest cell, ratios kept ; `span` is the sum of the cell sizes. */
    _evenBandThickness(grid, on)
    {
        let { along, across } = on;
        let longest = 0;

        for (let i = 0; i < grid.length; i++)
        {
            let band = grid[i];
            let thickness = this._thinnestOf(band, across);

            band.span = 0;

            for (let j = 0; j < band.length; j++)
            {
                let cell = band[j];

                if (cell[across] > thickness)
                {
                    cell[along] = cell[along] * thickness / cell[across];
                    cell[across] = thickness;
                }

                band.span += cell[along];
            }

            longest = Math.max(longest, band.span);
        }

        return longest;
    }

    /** The thinnest cell of a band. Zero stays the "no thickness" answer, so no Infinity seed here. */
    _thinnestOf(band, across)
    {
        let thinnest = 0;

        for (let j = 0; j < band.length; j++)
            thinnest = thinnest ? Math.min(thinnest, band[j][across]) : band[j][across];

        return thinnest;
    }

    /** Bands shorter than `longest` scaled up to it so they end flush ; recomputes `thickness`. */
    _stretchBands(grid, on, longest)
    {
        let { along, across } = on;

        for (let i = 0; i < grid.length; i++)
        {
            let band = grid[i];
            let scale = (band.span < longest) ? (longest / band.span) : 1;

            for (let j = 0; j < band.length; j++)
            {
                band[j][along] *= scale;
                band[j][across] *= scale;
            }

            band.thickness = this._thinnestOf(band, across);
        }
    }

    /** Cells end to end inside their band, bands stacked by `thickness`, `margin` between each pair. */
    _placeCells(grid, on, margin)
    {
        let { along, alongAt, acrossAt } = on;
        let acrossPos = 0, acrossMargin = 0;

        for (let i = 0; i < grid.length; i++)
        {
            let band = grid[i];
            let alongPos = 0, alongMargin = 0;

            for (let j = 0; j < band.length; j++)
            {
                let cell = band[j];

                if (j > 0)
                    alongMargin += margin;

                cell[acrossAt] = acrossPos + acrossMargin;
                cell[alongAt] = alongPos + alongMargin;

                alongPos += cell[along];
            }

            acrossMargin += margin;

            if (band.thickness)        // zero means an empty band : nothing to step over
                acrossPos += band.thickness;
        }
    }

    /** Cells pushed apart for the gutters ; `span` is now the band's far edge, gutters included. */
    _spaceOutBands(grid, on, margin)
    {
        let { along, alongAt, acrossAt } = on;
        let longest = 0, acrossMargin = 0;

        for (let i = 0; i < grid.length; i++)
        {
            let band = grid[i];
            let alongMargin = 0;

            band.span = 0;

            for (let j = 0; j < band.length; j++)
            {
                let cell = band[j];

                if (j > 0)
                    alongMargin += margin;

                cell[alongAt] += alongMargin;
                cell[acrossAt] += acrossMargin;

                band.span = Math.max(band.span, cell[alongAt] + cell[along]);
            }

            acrossMargin += margin;
            longest = Math.max(longest, band.span);
        }

        return longest;
    }

    /** Every split laid out on one axis. The grids are built here : _createLayout works one in place. */
    _layoutsFor(photos, combinations, page, on)
    {
        let grids = this._gridsFor(photos, combinations);
        let layouts = [];

        for (let i = 0; i < grids.length; i++)
            layouts.push( this._createLayout(grids[i], page, on) );

        return layouts;
    }

    /** Which way a grid's bands run : `along` is a band's own direction, `across` the one they stack on. */
    get ROWS()
    {
        return { along : "width", across : "height", alongAt : "x", acrossAt : "y" };
    }

    get COLUMNS()
    {
        return { along : "height", across : "width", alongAt : "y", acrossAt : "x" };
    }

    _scaleGrid(grid, page, center)
    {
        let scale = 1;
        let xOffset = 0;
        let yOffset = 0;
        let area = this._calculateBoundingBox(grid.flat());

        scale = (area.width > area.height) ?  (page.width - page.outer) / area.width : (page.height - page.outer) / area.height;
        
        if (area.height * scale > page.height)
            scale = (page.height - page.outer) / area.height;

        if (area.width * scale > page.width)
            scale = (page.width - page.outer) / area.width;
        
        if (center)            
        {
            xOffset = page.x - area.x*scale + (page.width - area.width*scale) / 2;
            yOffset = - area.y*scale + (page.height - area.height*scale) / 2;
        }

        for(let i = 0; i < grid.length; i++)
        {
            let row = grid[i];
            for(let j = 0; j < row.length; j++)
            {
                let cell = row[j];

                cell.width *= scale;
                cell.height *= scale;
                cell.x = (cell.x * scale) + xOffset;
                cell.y = (cell.y * scale) + yOffset;
            }
        }

        return grid;
    }

    /**
     * Every split of `max` into `dimension` non-empty bands, lexicographic, without dead branches.
     * Filtering all max^dimension tuples gives the same list in the same order, and took five
     * seconds at twenty-six photos -- that is what stopped a page at ten.
     */
    _findSumPermutations(dimension, max)
    {
        const results = [];
        const current = new Array(dimension);

        const walk = (position, remaining) =>
        {
            const left = dimension - position;

            if (left == 1)
            {
                current[position] = remaining;
                results.push(current.slice());
                return;
            }

            // leave at least one photo to each of the positions still to fill
            for (let i = 1; i <= remaining - (left - 1); i++)
            {
                current[position] = i;
                walk(position + 1, remaining - i);
            }
        };

        if (dimension >= 1 && max >= dimension)
            walk(0, max);

        return results;
    }

    /** The band splits to try : every one while they fit the pool, a sample beyond. */
    _combinationsFor(photos)
    {
        let count = photos.length;
        let pool = fx.get("settings").layoutsCandidatePool;
        let rowCounts = this._rowCountsFor(count);
        let share = Math.max(1, Math.floor(pool / rowCounts.length));
        let combinations = [];

        for (let i = 0; i < rowCounts.length; i++)
        {
            let k = rowCounts[i];
            let total = this._countCompositions(count, k, share);

            // the splits depend on k and the count alone, never on the photos : seeding from the
            // count is enough to draw the same pool every time, and every set of N draws the same
            let part = (total <= share) ? this._findSumPermutations(k, count)
                                       : this._sampleCompositions(k, count, share, count * 7919 + k);

            for (let j = 0; j < part.length; j++)
                combinations.push(part[j]);
        }

        return combinations;
    }

    /** How many bands to try : ceil(sqrt(N)), plus one either side once the page is crowded. */
    _rowCountsFor(count)
    {
        // a pair : one row holds it side by side, the split the square-ish guess never proposes
        if (count == 2)
            return [2, 1];

        let base = 1;
        while (base * base < count)
            base++;

        if (!this._isDense(count))
            return [base];

        let rowCounts = [];
        for (let k = base - 1; k <= base + 1; k++)
            if (k >= 1 && k <= count)
                rowCounts.push(k);

        return rowCounts;
    }

    /** C(count - 1, k - 1), abandoned as soon as it passes `limit` so a huge count costs nothing. */
    _countCompositions(count, k, limit)
    {
        let n = count - 1;
        let r = k - 1;

        if (r < 0 || r > n)
            return 0;

        r = Math.min(r, n - r);

        let total = 1;
        for (let i = 1; i <= r; i++)
        {
            total = total * (n - r + i) / i;

            if (total > limit)
                return limit + 1;
        }

        return total;
    }

    /**
     * `limit` splits, drawn identically every call. Even ones keep the pictures a decent size,
     * random ones fill the page better ; both are offered and the scoring picks.
     */
    _sampleCompositions(k, count, limit, seed)
    {
        let seen = new Set();
        let combinations = [];

        let add = (parts) =>
        {
            let key = parts.join(",");

            if (!seen.has(key))
            {
                seen.add(key);
                combinations.push(parts);
            }
        };

        this._evenCompositions(k, count, Math.max(1, Math.round(limit / 4))).forEach(add);

        let random = this._random(seed);

        // the draws repeat once most of the splits are in hand : stop trying rather than spin
        let guard = limit * 20;

        while (combinations.length < limit && guard-- > 0)
            add(this._randomComposition(k, count, random));

        return combinations;
    }

    /** The near-even splits : count % k rows of ceil(count / k), the rest of floor(count / k). */
    _evenCompositions(k, count, limit)
    {
        let high = Math.ceil(count / k);
        let low = Math.floor(count / k);
        let combinations = [];
        let parts = [];

        let walk = (position, highLeft) =>
        {
            if (combinations.length >= limit)
                return;

            if (position == k)
            {
                if (highLeft == 0)
                    combinations.push(parts.slice());

                return;
            }

            if (highLeft > 0)
            {
                parts.push(high);
                walk(position + 1, highLeft - 1);
                parts.pop();
            }

            if (low >= 1 && k - position - 1 >= highLeft)
            {
                parts.push(low);
                walk(position + 1, highLeft);
                parts.pop();
            }
        };

        walk(0, count - low * k);

        return combinations;
    }

    /** One split, from k - 1 distinct cut points taken in the row of photos. */
    _randomComposition(k, count, random)
    {
        let cuts = new Set();

        while (cuts.size < k - 1)
            cuts.add(1 + Math.floor(random() * (count - 1)));

        let sorted = [...cuts].sort((a, b) => a - b);
        let parts = [];
        let previous = 0;

        for (let i = 0; i < sorted.length; i++)
        {
            parts.push(sorted[i] - previous);
            previous = sorted[i];
        }

        parts.push(count - previous);

        return parts;
    }

    /** xorshift32 : integers only, so every browser draws exactly the same sequence. */
    _random(seed)
    {
        let state = (seed >>> 0) || 0x9e3779b9;

        return () =>
        {
            state ^= state << 13; state >>>= 0;
            state ^= state >>> 17;
            state ^= state << 5;  state >>>= 0;

            return state / 4294967296;
        };
    }

    // -------------------------------------------------------------- //

    _filterLayouts(layouts, photos)
    {
        let unique = [];
        const seenLayouts = new Set();
        let count = photos.length;

        let dense = this._isDense(count);

        layouts.forEach(layout => {

            layout = layout.sort( (a, b) => a.x - b.x || a.y - b.y );

            // the fingerprint and the smallest picture in one pass, and the score worked out once
            // per layout : the sort used to recompute a bounding box on every comparison
            let key = "";
            let smallest = Infinity, largest = 0;

            layout.forEach( rect => {
                key += `${Math.round(rect.x)}|${Math.round(rect.y)}|${Math.round(rect.width)}|${Math.round(rect.height)}|`;
                smallest = Math.min(smallest, rect.width, rect.height);
                largest = Math.max(largest, rect.width, rect.height);
            });

            if (!seenLayouts.has(key)) {
                seenLayouts.add(key);
                unique.push({ layout : layout, score : this._score(layout, smallest, largest, dense) });
            }
        });

        unique = unique.sort( (a, b) => b.score - a.score );

        if (dense)
            unique = this._preferVariedLayouts(unique);

        let max = this._maxLayouts(count);

        if (unique.length > max && count > 2)
            unique.splice(max, unique.length);

        return unique.map( entry => entry.layout );
    }

    /**
     * A band gives every picture the same thickness, so two photos of one shape give two identical
     * cells and rows look striped. Past VARIETY_TRIGGER, the least striped of the close-scoring
     * layouts leads instead. Only then : pushing an already varied one costs coverage for nothing.
     *
     * Never at the cost of the page, though : VARIETY_TOLERANCE lets through a layout scoring a
     * tenth below the best, and a tenth of score is room enough for a block standing well clear
     * of the paper edge. A candidate only leads here if its own outer band is no wider than the
     * one it displaces -- breaking up the rows is never paid for with the white around them.
     */
    _preferVariedLayouts(entries)
    {
        if (entries.length < 2)
            return entries;

        let striped = this._striping(entries[0].layout);

        if (striped <= this.VARIETY_TRIGGER)
            return entries;

        let limit = entries[0].score * this.VARIETY_TOLERANCE;
        let band = this._outerBand(entries[0].layout);
        let pick = 0;

        for (let i = 1; i < entries.length && entries[i].score >= limit; i++)
        {
            let other = this._striping(entries[i].layout);

            if (other < striped && this._outerBand(entries[i].layout) <= band)
            {
                striped = other;
                pick = i;
            }
        }

        if (pick > 0)
            entries.unshift( entries.splice(pick, 1)[0] );

        return entries;
    }

    /** The widest of the two white bands this layout would leave at the edge of the page. */
    _outerBand(layout)
    {
        let box = this._calculateBoundingBox(layout);

        if (!box)
            return Infinity;

        return Math.max((this._pageWidth - box.width) / 2, (this._pageHeight - box.height) / 2);
    }

    /** The share of neighbours in a row that carry the same shape, 0 (all different) to 1 (all alike). */
    _striping(layout)
    {
        let rows = this._bandsOf(layout, "y");
        let same = 0, pairs = 0;

        for (let i = 0; i < rows.length; i++)
        {
            let row = rows[i].slice().sort( (a, b) => a.x - b.x );

            for (let j = 1; j < row.length; j++)
            {
                pairs++;

                if (Math.abs(row[j - 1].width / row[j - 1].height - row[j].width / row[j].height) < 0.02)
                    same++;
            }
        }

        return pairs ? (same / pairs) : 0;
    }

    /** Past this share of look-alike neighbours a layout reads as stripes and is worth swapping out. */
    get VARIETY_TRIGGER()
    {
        return 0.25;
    }

    /** How much score a less striped layout may give up to lead instead. */
    get VARIETY_TOLERANCE()
    {
        return 0.9;
    }

    /**
     * Inside the frozen range, the area of the bounding box and nothing else.
     *
     * Past it, the pictures' own area -- spreading bands apart widens the box without growing a
     * picture -- weighted by the smallest side over the largest. Measured against an ideal cell
     * instead, evenness saturates and a layout with one huge picture scores as if it were regular.
     */
    _score(layout, smallest, largest, dense)
    {
        if (!dense)
        {
            let box = this._calculateBoundingBox(layout);

            return box.width * box.height;
        }

        // the pictures' own area, not the box around them : spreading the rows apart to fit the
        // page widens the box without making a single picture bigger, so a box would reward a
        // layout for its gutters -- a twenty-photo page scored well while covering 39% of itself
        let covered = 0;
        for (let i = 0; i < layout.length; i++)
            covered += layout[i].width * layout[i].height;

        let band = this.EVENNESS_BAND;

        return covered * (1 - band + band * (smallest / largest));
    }

    /**
     * How much of the score evenness may move. High on purpose : the pictures should be of a size,
     * and the coverage lost to that is left as white rather than taken back by cropping. It only
     * reorders candidates, so it cannot do better than the pool holds : evening the band sizes out
     * for real has to happen in _sampleCompositions.
     */
    get EVENNESS_BAND()
    {
        return 0.85;
    }

    /**
     * The same layouts with their bands pushed apart until the block takes the shape of the page.
     * The gutter is the one length nothing depends on, so widening it on one axis reshapes the
     * block without touching a picture. Offered beside the originals, never instead.
     */
    _gutterVariants(layouts, page)
    {
        let variants = [];
        let limit = page.margin * this.GUTTER_MAX;

        for (let i = 0; i < layouts.length; i++)
        {
            let rows = this._bandsOf(layouts[i], "y");
            let columns = this._bandsOf(layouts[i], "x");

            let spread = this._spread(layouts[i], rows, page, "y");
            if (spread && spread.gutter <= limit)
                variants.push(spread.layout);

            spread = this._spread(layouts[i], columns, page, "x");
            if (spread && spread.gutter <= limit)
                variants.push(spread.layout);
        }

        return variants;
    }

    /** The cells grouped by the edge they share : the rows of a layout, or its columns. */
    _bandsOf(layout, axis)
    {
        let bands = {};

        for (let i = 0; i < layout.length; i++)
        {
            let key = Math.round(layout[i][axis] * 100);

            if (!bands[key])
                bands[key] = [];

            bands[key].push(layout[i]);
        }

        return Object.keys(bands).sort((a, b) => a - b).map((key) => bands[key]);
    }

    /**
     * Bands pushed apart to the page's proportions. Null when there are fewer than two bands, or
     * one cell per band, or the block would have to be pulled tighter than the usual gutter.
     */
    _spread(layout, bands, page, axis)
    {
        if (bands.length < 2 || bands.length >= layout.length)
            return null;

        let across = (axis == "y") ? "height" : "width";
        let other = (axis == "y") ? "width" : "height";
        let inner = { width : page.width - page.outer, height : page.height - page.outer };
        let wanted = (axis == "y") ? (inner.height / inner.width) : (inner.width / inner.height);

        let box = this._calculateBoundingBox(layout);
        let stacked = bands.reduce((total, band) => total + band[0][across], 0);
        let gutter = (wanted * box[other] - stacked) / (bands.length - 1);

        // a variant adds space, it never takes it : squeezing the bands tighter than the usual
        // gutter is what left fifty photos touching each other by a tenth of a millimetre
        if (!(gutter >= page.margin))
            return null;

        let spread = [];
        let at = 0;

        for (let i = 0; i < bands.length; i++)
        {
            for (let j = 0; j < bands[i].length; j++)
            {
                let cell = bands[i][j];

                spread.push({ photo : cell.photo,
                              x : (axis == "y") ? cell.x : at,
                              y : (axis == "y") ? at : cell.y,
                              width : cell.width, height : cell.height });
            }

            at += bands[i][0][across] + gutter;
        }

        return { layout : this._scaleGrid([spread], page, true).flat(), gutter : gutter };
    }

    /**
     * Gutter once a page is crowded : fifty photos carry eight gutters across where ten carry two.
     * Flat rather than following the picture size, which would reach 1.9 and read as a seam.
     */
    get DENSE_MARGIN()
    {
        return 2.5;
    }

    /** White kept around the whole block, at the edge of the page. Not the gutter between pictures. */
    get PAGE_MARGIN()
    {
        return 20;
    }


    /**
     * The same white, on a page carrying more than the frozen range. A crowded page reads as a
     * block of pictures rather than as one picture placed on a sheet, so there is none of it : the
     * block starts at the edge of the sheet, LOST_BORDER and all.
     *
     * That band is cut away, so the outermost pictures lose it -- which is what a bleed is for.
     * Stopping at the trim instead leaves a white line down one side the moment the trimmer is a
     * millimetre out, and a bled picture only loses a millimetre of itself.
     */
    get DENSE_PAGE_MARGIN()
    {
        return 0;
    }

        get LOST_BORDER()
    {
        return 5;
    }


    /**
     * The white kept against the fold, measured from the edge of the sheet, on one edge only --
     * zero, so a crowded page bleeds on all four and the fold is not special after all.
     *
     * The edge is still worked out and still passed in, because it is a fact about the page that
     * the generator had no way of knowing and the callers did : a page is laid out on its own
     * (`creation.pageWidth`, never a spread), so the fold is on the opposite edge from the side
     * the page sits on, and the cover folds at its spine like any other. This is a figure to
     * raise again if a printed copy shows the fold eating the pictures along that edge -- it is
     * not dead weight to delete.
     */
    get FOLD_MARGIN()
    {
        return 0;
    }

    /** What the fold edge insets on top of the shared margin, so it ends up FOLD_MARGIN from the sheet. */
    _foldMargin(fold, dense)
    {
        if (!dense || (fold != "left" && fold != "right"))
            return 0;

        return Math.max(0, this.FOLD_MARGIN - this.DENSE_PAGE_MARGIN / 2);
    }    /** Gutter between two pictures, up to layoutsLegacyUpTo photos. See DENSE_MARGIN past it. */
    get PICTURE_MARGIN()
    {
        return 4;
    }

    /** How far a gutter may be stretched to fit the page, as a multiple of the usual one. */
    get GUTTER_MAX()
    {
        return 1.5;
    }

    /**
     * The white left around the block pulled in, by stretching it towards the printable area a
     * little more on one axis than the other.
     *
     * _scaleGrid has already grown the block as far as it can go without cropping, so one of the
     * two bands is down to the page margin and the other is whatever the photos' ratios left. Each
     * axis is allowed a further 1 + MAX_CROP here, and never more than its own room.
     *
     * It pulls a block back in as readily as it pushes one out, which it did not use to : that
     * margin was white to be won back, and it is the trim line now. _scaleGrid takes its scale
     * from whichever side of the block is longer and only corrects when the block overruns the
     * *page*, never the margin, so a candidate can arrive here hanging over the cut -- 27% of the
     * leading ones did. Letting that through costs a photograph its edge, not some white.
     *
     * Every cell carries its photo's own ratio, so the gap between the two scales crops every
     * picture by that same fraction, at most 1 - 1 / (1 + MAX_CROP). The gutters stretch with the
     * block, so they too part between the axes by it -- at five percent, a tenth of a millimetre.
     *
     * Runs after the ranking, so it changes how a layout sits on the page, never which one wins.
     */
    _reduceMargins(layouts, page)
    {
        let crop = this.MAX_CROP;

        if (!crop)
            return;

        let roomWidth = page.width - page.outer;
        let roomHeight = page.height - page.outer;

        for (let i = 0; i < layouts.length; i++)
        {
            let layout = layouts[i];
            let box = this._calculateBoundingBox(layout);

            if (!box || !(box.width > 0) || !(box.height > 0))
                continue;

            let scaleX = Math.min(roomWidth / box.width, 1 + crop);
            let scaleY = Math.min(roomHeight / box.height, 1 + crop);
            let left = page.x + (page.width - box.width * scaleX) / 2;
            let top = (page.height - box.height * scaleY) / 2;

            for (let j = 0; j < layout.length; j++)
            {
                let cell = layout[j];

                cell.x = left + (cell.x - box.x) * scaleX;
                cell.y = top + (cell.y - box.y) * scaleY;
                cell.width *= scaleX;
                cell.height *= scaleY;
            }
        }
    }

    /**
     * How far each axis of _reduceMargins may stretch, which bounds how much of a picture it may
     * take : at most 1 - 1 / 1.09, a twelfth of one side, shared between its two opposite edges.
     * It is the only place anything is ever cropped, and the only lever that reaches the band a
     * square page keeps -- there the block is a tenth too narrow for the sheet whatever the pool
     * holds, and the better fitting candidates cost an eightfold spread in picture size.
     */
    get MAX_CROP()
    {
        return 0.09;
    }

    /** Thirty layouts, fewer past layoutsCellBudget cells, which only an uncapped poster reaches. */
    _maxLayouts(count)
    {
        return Math.max(8, Math.min(30, Math.floor(fx.get("settings").layoutsCellBudget / count)));
    }

    _improveLayouts(layouts)
    {
        let improvedLayouts = [];
        for(let i = 0; i < layouts.length; i++)
        {
            let layout = layouts[i];


            let components = [];
            for(let j = 0; j < layout.length; j++)
            {   
                components.push( new models.ThemeLayoutComponent( {
                                                                        component_type : "photo",
                                                                        x : layout[j].x,
                                                                        y : layout[j].y,
                                                                        width : layout[j].width,
                                                                        height : layout[j].height,
                                                                        width_inner : 0,
                                                                        height_inner : 0   
                                                                    }) );            
            }


            improvedLayouts.push( new models.ThemeLayout({components : components}) );
        }
        
       
        return improvedLayouts;
    }

    _calculateBoundingBox(rectangles) {
        if (rectangles.length === 0) {
            return null; // No rectangles to process
        }
    
        // Initialize the bounding box with the first rectangle's properties
        let left = rectangles[0].x;
        let top = rectangles[0].y;
        let right = rectangles[0].x + rectangles[0].width;
        let bottom = rectangles[0].y + rectangles[0].height;
    
        // Loop through each rectangle to find the extreme coordinates
        for (let i = 1; i < rectangles.length; i++) {
            const rect = rectangles[i];
            left = Math.min(left, rect.x);
            top = Math.min(top, rect.y);
            right = Math.max(right, rect.x + rect.width);
            bottom = Math.max(bottom, rect.y + rect.height);
        }
    
        // Return the bounding box as a new rectangle object
        return {
            x: left,
            y: top,
            width: right - left,
            height: bottom - top
        };
    }

    // -------------------------------------------------------------- //

   
    
    
});